import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, ShieldAlert, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProductionSettings() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get("/production/admin/reset/preview");
      setPreview(res.data);
    } catch (e) {
      toast.error("Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const runReset = async () => {
    setResetting(true);
    try {
      const res = await api.post("/production/admin/reset?confirm=RESET");
      setLastResult(res.data);
      toast.success(`Reset complete — ${res.data.created.raw_materials} raw materials imported`);
      setConfirmOpen(false);
      setConfirmText("");
      await loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl space-y-6" data-testid="production-settings-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Production Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Administrative controls for the Production Operations module.</p>
      </div>

      {/* Account settings — placeholder card */}
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="font-heading font-semibold text-gray-900 mb-1">Account</h2>
        <p className="text-xs text-gray-500 mb-4">Manage your login credentials.</p>
        <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
          Change Password is coming soon — the current auth system needs a bcrypt-backed store swap first.
          Ping the team to schedule that integration.
        </div>
      </section>

      {/* Reset & Re-import */}
      <section className="bg-white rounded-lg border-2 border-red-200 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="h-5 w-5 text-red-700" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-red-900">Reset & Re-import Production Data</h2>
            <p className="text-xs text-red-800 mt-0.5">
              Wipes ALL raw materials, purchases, vendors, and the stock ledger &mdash;
              then re-imports every Retail product whose name starts with <b>Rg</b>.
              This is <b>not reversible</b>.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading preview…</div>
        ) : preview ? (
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-red-800 mb-2">Will Wipe</p>
              {Object.entries(preview.will_wipe).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-0.5">
                  <span className="text-red-900">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono font-bold text-red-700">{v}</span>
                </div>
              ))}
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800 mb-2">Will Import</p>
              {Object.entries(preview.will_import).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-0.5">
                  <span className="text-emerald-900">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono font-bold text-emerald-700">{v}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {lastResult && (
          <div className="mb-4 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs flex items-start gap-2">
            <Check className="h-4 w-4 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900">Reset complete</p>
              <p className="text-emerald-800">
                Wiped {Object.values(lastResult.wiped).reduce((a, b) => a + b, 0)} rows &middot;
                Imported {lastResult.created.raw_materials} raw materials + {lastResult.created.rm_vendors} vendors
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadPreview} disabled={loading} data-testid="reset-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh Counts
          </Button>
          <Button
            onClick={() => { setConfirmText(""); setConfirmOpen(true); }}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="reset-open-confirm-btn"
          >
            <AlertTriangle className="h-4 w-4 mr-2" /> Reset & Re-import
          </Button>
        </div>
      </section>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-900">
              <ShieldAlert className="h-5 w-5" /> Confirm Reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              This will <b>wipe</b> all raw materials, purchases, vendors and the stock ledger,
              then <b>re-import</b> {preview?.will_import?.retail_rg_products ?? "…"} raw materials
              from Retail.
            </p>
            <p className="text-gray-500 text-xs">
              Type <span className="font-mono font-bold text-red-700">RESET</span> below to confirm.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET"
              className="font-mono"
              data-testid="reset-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={runReset}
              disabled={confirmText !== "RESET" || resetting}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="reset-execute-btn"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Wipe & Re-import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
